/**
 * LinkThreadDialog Component
 * "Other senders" — files an UNMATCHED email thread under a tenant, a vendor, or a vendor created
 * on the spot (BR-114, BR-126). Linking always recognizes the sender's address, so there is no
 * "also recognize" choice; a line states the effect instead. A tenant cannot be created here.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listUnits,
  listTenantsByUnit,
  linkThread,
  ApiClientError,
  type UnitWithRelations,
  type TenantWithDetails,
  type CounterpartyKind,
} from '../../lib/api';
import { listVendors, type Vendor } from '../../api/vendors.api';
import { Modal } from '../Modal';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { SegmentedRadioGroup } from './SegmentedRadioGroup';
import { NewVendorForm, type NewVendorValues } from './NewVendorForm';
import { unitOptionLabel, tenantDisplayName, isReachableTenant } from './email-labels';

export interface LinkedCounterparty {
  kind: CounterpartyKind;
  name: string;
}

interface LinkThreadDialogProps {
  isOpen: boolean;
  threadId: string;
  senderAddress: string;
  onClose: () => void;
  onLinked: (counterparty: LinkedCounterparty) => void;
  /** Called after a 409 so the page can tell whether the thread itself was already linked or discarded. */
  onConflict?: () => void;
}

type LinkTarget = 'tenant' | 'vendor';
type VendorMode = 'choose' | 'new';

const LINK_TARGET_OPTIONS = [
  { value: 'tenant', label: 'Tenant', testId: 'link-target-tenant' },
  { value: 'vendor', label: 'Vendor', testId: 'link-target-vendor' },
] as const;

const optionClass = (selected: boolean): string =>
  `w-full p-4 mb-2 border rounded-md text-left cursor-pointer flex items-center gap-3 min-h-[44px] transition-colors ${
    selected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-accent'
  }`;

function RadioMark({ selected }: { selected: boolean }): JSX.Element {
  return (
    <div
      className={`w-[18px] h-[18px] border-2 rounded-full flex items-center justify-center shrink-0 text-xs ${
        selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'
      }`}
      aria-hidden="true"
    >
      {selected ? '✓' : ''}
    </div>
  );
}

function vendorSubtitle(vendor: Vendor): string {
  const trades = vendor.trades.join(', ');
  const email = vendor.email ?? 'No email on file';
  return trades ? `${trades} · ${email}` : email;
}

export function LinkThreadDialog({
  isOpen,
  threadId,
  senderAddress,
  onClose,
  onLinked,
  onConflict,
}: LinkThreadDialogProps): JSX.Element {
  const [target, setTarget] = useState<LinkTarget>('tenant');
  const [vendorMode, setVendorMode] = useState<VendorMode>('choose');
  const [tenantStep, setTenantStep] = useState<'unit' | 'tenant'>('unit');
  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [tenants, setTenants] = useState<TenantWithDetails[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<TenantWithDetails | null>(null);
  const [vendors, setVendors] = useState<Vendor[] | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [vendorSearch, setVendorSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  // Load failures (units/tenants/vendors fetch) get a "Try again" retry affordance; submit
  // failures (409/404s) don't — retrying those means resubmitting, not reloading.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setTarget('tenant');
    setVendorMode('choose');
    setTenantStep('unit');
    setUnits([]);
    setTenants([]);
    setSelectedUnitId(null);
    setSelectedTenant(null);
    setVendors(null);
    setSelectedVendor(null);
    setVendorSearch('');
    setLoadError(null);
    setSubmitError(null);
  }, []);

  const loadUnits = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const { units: loadedUnits } = await listUnits();
      setUnits(loadedUnits);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load units');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadVendors = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const { vendors: loaded } = await listVendors();
      setVendors(loaded.filter((v) => v.isActive));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load vendors');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      reset();
      void loadUnits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleTargetChange = useCallback(
    (next: LinkTarget) => {
      setTarget(next);
      setSubmitError(null);
      setLoadError(null);
      if (next === 'vendor' && vendors === null) void loadVendors();
    },
    [vendors, loadVendors]
  );

  const handleUnitSelect = useCallback(async (unitId: string) => {
    try {
      setIsLoading(true);
      setLoadError(null);
      setSelectedUnitId(unitId);
      const { tenants: loadedTenants } = await listTenantsByUnit(unitId);
      setTenants(loadedTenants.filter(isReachableTenant));
      setTenantStep('tenant');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Re-runs whichever load most recently failed.
  const handleRetryLoad = useCallback(() => {
    if (target === 'vendor') void loadVendors();
    else if (tenantStep === 'tenant' && selectedUnitId) void handleUnitSelect(selectedUnitId);
    else void loadUnits();
  }, [target, tenantStep, selectedUnitId, handleUnitSelect, loadUnits, loadVendors]);

  const handleBackToUnits = useCallback(() => {
    setTenantStep('unit');
    setTenants([]);
    setSelectedTenant(null);
    setLoadError(null);
  }, []);

  const submit = useCallback(
    async (body: Parameters<typeof linkThread>[1], fallbackName: string, kind: CounterpartyKind) => {
      try {
        setIsLinking(true);
        setSubmitError(null);
        const thread = await linkThread(threadId, body);
        const linked = thread.counterparties[0];
        onLinked({ kind: linked?.kind ?? kind, name: linked?.name ?? fallbackName });
      } catch (err) {
        // apiFetch carries the server's message as Error#message, so the 409s (thread already
        // filed, address belongs to another counterparty) surface verbatim.
        setSubmitError(err instanceof Error ? err.message : 'Failed to link thread');
        if (err instanceof ApiClientError && err.status === 409) onConflict?.();
      } finally {
        setIsLinking(false);
      }
    },
    [threadId, onLinked, onConflict]
  );

  const handleSubmit = useCallback(() => {
    if (target === 'tenant' && selectedTenant) {
      void submit({ tenantId: selectedTenant.id }, tenantDisplayName(selectedTenant), 'tenant');
    } else if (target === 'vendor' && selectedVendor) {
      void submit({ vendorId: selectedVendor.id }, selectedVendor.name, 'vendor');
    }
  }, [target, selectedTenant, selectedVendor, submit]);

  const handleCreateVendor = useCallback(
    (values: NewVendorValues) => {
      void submit(
        {
          newVendor: {
            name: values.name,
            ...(values.trades.length > 0 && { trades: values.trades }),
            ...(values.email && { email: values.email }),
          },
        },
        values.name,
        'vendor'
      );
    },
    [submit]
  );

  const filteredVendors = useMemo(() => {
    const q = vendorSearch.trim().toLowerCase();
    if (!vendors) return [];
    if (!q) return vendors;
    return vendors.filter(
      (v) => v.name.toLowerCase().includes(q) || v.trades.some((t) => t.toLowerCase().includes(q))
    );
  }, [vendors, vendorSearch]);

  if (!isOpen) return <></>;

  const isNewVendor = target === 'vendor' && vendorMode === 'new';
  const canSubmit = target === 'tenant' ? selectedTenant !== null : selectedVendor !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isNewVendor ? 'Add a new vendor' : 'Link this email'}
      data-testid="link-thread-to-tenant-dialog"
      showGrabHandle
    >
      {!isNewVendor && (
        <div className="flex flex-col gap-4 mb-4">
          <p className="text-sm text-muted-foreground m-0">
            File the email from <span className="font-medium text-foreground">{senderAddress}</span> under:
          </p>
          <SegmentedRadioGroup
            ariaLabel="Link to"
            options={[...LINK_TARGET_OPTIONS]}
            value={target}
            onChange={handleTargetChange}
            testId="link-target-group"
            className="self-start"
          />
        </div>
      )}

      {loadError && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          data-testid="link-thread-error"
        >
          <span>{loadError}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRetryLoad}
            data-testid="link-thread-retry-button"
            className="min-h-[44px]"
          >
            Try again
          </Button>
        </div>
      )}

      {submitError && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          data-testid="link-thread-submit-error"
        >
          {submitError}
        </div>
      )}

      {isNewVendor ? (
        <NewVendorForm
          senderAddress={senderAddress}
          submitting={isLinking}
          onBack={() => setVendorMode('choose')}
          onCancel={handleClose}
          onSubmit={handleCreateVendor}
        />
      ) : (
        <>
          {target === 'tenant' ? (
            <TenantPicker
              step={tenantStep}
              isLoading={isLoading}
              units={units}
              tenants={tenants}
              selectedTenant={selectedTenant}
              onUnitSelect={handleUnitSelect}
              onTenantSelect={setSelectedTenant}
            />
          ) : (
            <VendorPicker
              isLoading={isLoading}
              vendors={filteredVendors}
              search={vendorSearch}
              onSearchChange={setVendorSearch}
              selectedVendor={selectedVendor}
              onSelect={setSelectedVendor}
              onAddNew={() => {
                setSubmitError(null);
                setVendorMode('new');
              }}
            />
          )}

          <p className="text-sm text-muted-foreground my-4" data-testid="link-thread-effect">
            All future email from{' '}
            <span className="font-medium text-foreground">{senderAddress}</span> will be filed under the{' '}
            {target} you choose.
            {target === 'vendor' && " We'll keep writing to the vendor's own email."}
          </p>

          <div className="flex gap-3 justify-end">
            {target === 'tenant' && tenantStep === 'tenant' && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBackToUnits}
                disabled={isLinking}
                data-testid="link-thread-back-button"
              >
                Back
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLinking}
              data-testid="link-thread-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || isLinking}
              data-testid="link-thread-submit"
            >
              {isLinking ? 'Linking...' : target === 'tenant' ? 'Link to tenant' : 'Link to vendor'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

interface TenantPickerProps {
  step: 'unit' | 'tenant';
  isLoading: boolean;
  units: UnitWithRelations[];
  tenants: TenantWithDetails[];
  selectedTenant: TenantWithDetails | null;
  onUnitSelect: (unitId: string) => void;
  onTenantSelect: (tenant: TenantWithDetails) => void;
}

function TenantPicker({
  step,
  isLoading,
  units,
  tenants,
  selectedTenant,
  onUnitSelect,
  onTenantSelect,
}: TenantPickerProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-2 py-2">
        {[...Array(step === 'unit' ? 4 : 3)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (step === 'unit') {
    return (
      <div className="max-h-[400px] overflow-auto">
        {units.length === 0 ? (
          <p className="text-center text-muted-foreground">No units found</p>
        ) : (
          units.map((unit) => (
            <button
              key={unit.id}
              type="button"
              onClick={() => onUnitSelect(unit.id)}
              className="w-full p-4 mb-2 border border-border rounded-md bg-card text-left cursor-pointer hover:bg-accent min-h-[44px]"
              data-testid={`link-unit-option-${unit.id}`}
            >
              <div className="font-semibold mb-1 text-foreground">{unitOptionLabel(unit)}</div>
              <div className="text-sm text-muted-foreground">
                {unit.address1}, {unit.city}, {unit.state}
              </div>
            </button>
          ))
        )}
      </div>
    );
  }
  return (
    <div className="max-h-[400px] overflow-auto" role="radiogroup" aria-label="Select tenant">
      {tenants.length === 0 ? (
        <p className="text-center text-muted-foreground">No tenants in this unit</p>
      ) : (
        tenants.map((tenant) => {
          const isSelected = selectedTenant?.id === tenant.id;
          return (
            <button
              key={tenant.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onTenantSelect(tenant)}
              className={optionClass(isSelected)}
              data-testid={`link-tenant-option-${tenant.id}`}
            >
              <RadioMark selected={isSelected} />
              <div>
                <div className="font-semibold text-foreground">{tenantDisplayName(tenant)}</div>
                {tenant.email && <div className="text-sm text-muted-foreground">{tenant.email}</div>}
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}

interface VendorPickerProps {
  isLoading: boolean;
  vendors: Vendor[];
  search: string;
  onSearchChange: (value: string) => void;
  selectedVendor: Vendor | null;
  onSelect: (vendor: Vendor) => void;
  onAddNew: () => void;
}

function VendorPicker({
  isLoading,
  vendors,
  search,
  onSearchChange,
  selectedVendor,
  onSelect,
  onAddNew,
}: VendorPickerProps): JSX.Element {
  return (
    <>
      <div className="mb-3">
        <label htmlFor="link-vendor-search" className="block text-sm font-medium text-foreground mb-1.5">
          Search vendors
        </label>
        <input
          id="link-vendor-search"
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Name or trade"
          data-testid="link-vendor-search"
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground min-h-[44px]"
        />
      </div>
      {isLoading ? (
        <div className="space-y-2 py-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <div className="max-h-[260px] overflow-auto" role="radiogroup" aria-label="Select vendor">
          {vendors.length === 0 ? (
            <p className="text-center text-muted-foreground" data-testid="link-vendor-empty">
              No vendors found
            </p>
          ) : (
            vendors.map((vendor) => {
              const isSelected = selectedVendor?.id === vendor.id;
              return (
                <button
                  key={vendor.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => onSelect(vendor)}
                  className={optionClass(isSelected)}
                  data-testid={`link-vendor-option-${vendor.id}`}
                >
                  <RadioMark selected={isSelected} />
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground">{vendor.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{vendorSubtitle(vendor)}</div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        onClick={onAddNew}
        data-testid="link-add-new-vendor"
        className="mt-1"
      >
        Add a new vendor
      </Button>
    </>
  );
}
